// Phase 3 C3 — CDD section for the client record.
//
// Rows: identity verification, PEP screen, UBO chain (legal-persons only),
// source of wealth. Pattern mirrors CddCaseSection: status pill + verifier
// metadata + per-row modal. UBO is a list (add/edit/delete).
//
// Permission gating: lateral.persons covers both view and edit (mirrors the
// existing client CRUD gate at server/src/routes/clients.js POST/PUT).

import React, { useState, useEffect } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import { openClientFolder } from "../lib/clientFolder";
// P17 Slice A — the identity-status derivation is the SHARED core source, the same
// one server/src/routes/cdd.js runs (the F1/F2 anti-drift collapse). Untyped .mjs,
// mirroring the coiMatch.mjs import (NameAdvisor.tsx / App.tsx).
import { deriveIdentityStatus } from "../lib/cddIdentity.mjs";
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  Clock,
  Ban,
  Users,
  Pencil,
  Plus,
  Trash2,
  Folder,
  ExternalLink,
} from "lucide-react";
import { CT, RAD, FS } from "../lib/tokens";
import { ModalLayout } from "../components/SharedUI";
import MenuSelect from "../components/MenuSelect"; // 154B §2
import { useConfirm } from "../components/useConfirmAndUndo";

function StatusPill({ status }: any) {
  const { t } = useTranslation();
  const map = {
    "not-applicable": {
      color: "var(--tm)",
      bg: "rgba(120,120,120,.15)",
      icon: Ban,
      labelKey: "cdd.status.not_applicable",
      fallback: "Not applicable",
    },
    required: {
      color: CT.c_ef4444,
      bg: "rgba(239,68,68,.15)",
      icon: AlertTriangle,
      labelKey: "cdd.status.required",
      fallback: "Required",
    },
    "in-progress": {
      color: CT.c_f59e0b,
      bg: "rgba(245,158,11,.15)",
      icon: Clock,
      labelKey: "cdd.status.in_progress",
      fallback: "In progress",
    },
    complete: {
      color: CT.c_10b981,
      bg: "rgba(16,185,129,.15)",
      icon: CheckCircle2,
      labelKey: "cdd.status.complete",
      fallback: "Complete",
    },
    stale: {
      color: CT.c_a16207,
      bg: "rgba(161,98,7,.15)",
      icon: Circle,
      labelKey: "cdd.status.stale",
      fallback: "Stale",
    },
  };
  const cfg = map[status as keyof typeof map] || map["required"];
  const Icon = cfg.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: RAD.r999,
        fontSize: FS.s10,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}40`,
      }}
    >
      <Icon size={11} />
      {t(cfg.labelKey, { fallback: cfg.fallback })}
    </span>
  );
}

const STATUSES = [
  "not-applicable",
  "required",
  "in-progress",
  "complete",
  "stale",
];
function StatusSelect({ value, onChange }: any) {
  const { t } = useTranslation();
  return (
    <MenuSelect
      className="field-input"
      value={value || "required"}
      onChange={(v) => onChange(v)}
      options={STATUSES.map(
        (s) =>
          [s, t("cdd.status." + s.replace("-", "_"), { fallback: s })] as const,
      )}
    />
  );
}

function RowModal({ title, onCancel, onSave, children }: any) {
  return (
    <ModalLayout
      title={title}
      onClose={onCancel}
      onSave={onSave}
      maxWidth={540}
    >
      {children}
    </ModalLayout>
  );
}

function formatDate(d: any) {
  if (!d) return "";
  const x = new Date(d);
  return `${String(x.getDate()).padStart(2, "0")}/${String(x.getMonth() + 1).padStart(2, "0")}/${x.getFullYear()}`;
}
function pname(co: any, id: any) {
  if (!id || !Array.isArray(co)) return "";
  const r = co.find((x) => x.id === id);
  return r
    ? [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email || "#" + id
    : "#" + id;
}

// BUG-A SWEEP — hoisted out of CddClientSection's render body. It was declared
// there and mounted as JSX, so every render of the CDD panel gave React a new
// component type for each due-diligence row and threw the row subtree away —
// including the open/closed state of anything nested inside it. `canEdit` and
// the collaborator list `co` arrive as props; nothing else changes.
const Row = ({ icon: Icon, title, status, vid, vat, onEdit, children, canEdit, co }: any) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 12px",
      marginBottom: 6,
      background: "var(--bi)",
      border: "1px solid var(--br)",
      borderRadius: RAD.r8,
    }}
  >
    <div style={{ marginTop: 2 }}>
      <Icon size={14} style={{ color: "var(--ac)" }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 2,
        }}
      >
        <span style={{ fontSize: FS.s12, fontWeight: 600 }}>{title}</span>
        <StatusPill status={status || "required"} />
        {vid && (
          <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
            {pname(co, vid)}
            {vat ? ` · ${formatDate(vat)}` : ""}
          </span>
        )}
      </div>
      {children && (
        <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>{children}</div>
      )}
    </div>
    {canEdit && (
      <button
        type="button"
        className="btn btn-ghost"
        style={{ padding: "3px 10px", fontSize: FS.s10 }}
        onClick={onEdit}
      >
        <Pencil size={10} />
      </button>
    )}
  </div>
);

export default function CddClientSection({ client, co }: any) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  const { can } = usePermissions();
  const canEdit = can("lateral.persons.edit");
  const isCompany = (client?.type || "").toLowerCase() === "company";

  const [state, setState] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<any>(null);

  useEffect(() => {
    if (!client?.id) return;
    setLoading(true);
    api
      .get("/cdd/client/" + client.id)
      .then((d) => setState(d))
      .catch((e) => {
        console.warn("[cdd-client] load failed", e);
      })
      .finally(() => setLoading(false));
  }, [client?.id]);

  if (loading)
    return (
      <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
        {t("common.loading", { fallback: "Loading…" })}
      </p>
    );
  if (!state) return null;

  const patchState = async (patch: any) => {
    try {
      const res = (await api.patch("/cdd/client/" + client.id, patch)) as Record<string, unknown>;
      setState((prev: any) => ({ ...prev, ...res }));
      setModal(null);
    } catch (e: any) {
      if (window.lxToast) window.lxToast("⚠ " + (e.message || "Save failed"));
    }
  };


  return (
    <div style={{ margin: "14px 0" }}>
      {/* Phase 41 (D3a) — "Client file" open link on the client record.
          state.clientRootFolder is SERVER-gated (null unless the viewer passes
          clientFolderGate — admin OR RP on a matter this client is linked to),
          so a truthy value here already means this viewer may open it. Opens
          via the shared resolve-and-open path (no second resolver). */}
      {state.clientRootFolder && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            marginBottom: 10,
            background: "var(--bi)",
            border: "1px solid var(--br)",
            borderRadius: RAD.r8,
          }}
        >
          <Folder size={14} style={{ color: "var(--ac)", flexShrink: 0 }} />
          <span
            style={{
              fontSize: FS.s12,
              fontWeight: 600,
              color: "var(--tp)",
              flex: 1,
              minWidth: 0,
            }}
          >
            {t("clientFile.label", { fallback: "Client file" })}
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            title={t("clientFile.open", { fallback: "Open client file" })}
            style={{
              padding: "2px 8px",
              fontSize: FS.s10,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              flexShrink: 0,
            }}
            onClick={() =>
              openClientFolder(client.id, state.clientRootFolder, t)
            }
          >
            <ExternalLink size={10} />
            {t("common.open", { fallback: "Open" })}
          </button>
        </div>
      )}
      <h3 style={{ margin: "0 0 8px", fontSize: FS.s14, fontWeight: 600 }}>
        {t("cdd.section.title", { fallback: "Client Due Diligence (CDD)" })}
      </h3>

      {/* Round 2 #10 — identity-row derived completeness for natural persons.
          Complete = name fields present AND nationality present AND at least one of
          (nationalRegister OR (idDocumentNumber AND idDocumentExpiresAt not past)).
          Verifier-stamped completion ('complete' stored status) still wins. */}
      {(() => {
        // P17 Slice A — derivation moved to the SHARED core source (single source
        // routes/cdd.js also runs). Pass the stored status through as-today (the
        // client does NOT floor to 'required' — the server does, at its call site).
        // isNatural stays for the detail render branch below.
        const isNatural = !isCompany;
        const derivedStatus = deriveIdentityStatus(client, state.identityStatus);
        return (
          <Row
          canEdit={canEdit}
          co={co}
            icon={CheckCircle2}
            title={t("cdd.row.identity", { fallback: "Identity verification" })}
            status={derivedStatus}
            vid={state.identityVerifiedById}
            vat={state.identityVerifiedAt}
            onEdit={() => setModal({ kind: "identity" })}
          >
            {isNatural && (
              <span>
                {client?.nationality ? client.nationality + " · " : ""}
                {client?.idDocumentNumber
                  ? client.idDocumentNumber
                  : client?.nationalRegister || "—"}
                {client?.idDocumentExpiresAt
                  ? " · exp " + formatDate(client.idDocumentExpiresAt)
                  : ""}
              </span>
            )}
          </Row>
        );
      })()}

      <Row
          canEdit={canEdit}
          co={co}
        icon={AlertTriangle}
        title={t("cdd.row.pep", { fallback: "PEP screen" })}
        status={state.pepStatus}
        vid={state.pepVerifiedById}
        vat={state.pepVerifiedAt}
        onEdit={() => setModal({ kind: "pep" })}
      >
        {state.pepIsPep === true && (
          <strong style={{ color: CT.c_ef4444 }}>
            {t("cdd.pep.flagged", { fallback: "PEP flagged" })}
          </strong>
        )}
        {state.pepScreenedAt && (
          <>{t("cdd.pep.screened_on", { d: formatDate(state.pepScreenedAt), fallback: " · screened {d}" })}</>
        )}
      </Row>

      {isCompany && (
        <div style={{ marginBottom: 6 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              background: "var(--bi)",
              border: "1px solid var(--br)",
              borderRadius: RAD.r8,
            }}
          >
            <Users size={14} style={{ color: "var(--ac)" }} />
            <span style={{ fontSize: FS.s12, fontWeight: 600, flex: 1 }}>
              {t("cdd.row.ubo", { fallback: "UBO chain" })}
            </span>
            <StatusPill status={state.ubosStatus || "required"} />
            {canEdit && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ padding: "3px 10px", fontSize: FS.s10 }}
                onClick={() => setModal({ kind: "ubo_new" })}
              >
                <Plus size={10} />{" "}
                {t("cdd.btn.add_ubo", { fallback: "Add UBO" })}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ padding: "3px 10px", fontSize: FS.s10 }}
                onClick={() => setModal({ kind: "ubos_status" })}
              >
                <Pencil size={10} />
              </button>
            )}
          </div>
          {(state.ubos || []).map((u: any) => (
            <div
              key={u.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                marginLeft: 22,
                fontSize: FS.s11,
                borderBottom: "1px solid var(--br)",
              }}
            >
              <span style={{ flex: 1 }}>
                {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                {u.ownershipPct ? ` · ${u.ownershipPct}%` : ""}
                {u.controlType ? ` · ${u.controlType}` : ""}
              </span>
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: "2px 6px", fontSize: FS.s9 }}
                  onClick={() => setModal({ kind: "ubo_edit", row: u })}
                >
                  <Pencil size={9} />
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  style={{
                    padding: "2px 6px",
                    fontSize: FS.s9,
                    background: "rgba(248,113,113,.1)",
                    color: "var(--rd)",
                    border: "1px solid rgba(248,113,113,.3)",
                  }}
                  onClick={() => {
                    askConfirm({
                      // 161C §3
                      title: t("confirm.delete_ubo_title", {
                        fallback: "Delete the UBO entry?",
                      }),
                      message: t("cdd.ubo.delete_confirm", {
                        fallback: "Delete this UBO entry?",
                      }),
                      confirmLabel: t("common.delete", { fallback: "Delete" }),
                      onConfirm: async () => {
                        try {
                          await api.del("/cdd/ubo/" + u.id);
                          setState((prev: any) => ({
                            ...prev,
                            ubos: prev.ubos.filter((x: any) => x.id !== u.id),
                          }));
                        } catch (e: any) {
                          // Phase 30 (D-3) — was a silent unhandled rejection.
                          window.__lxToast?.(
                            "Delete failed: " + (e?.message || e),
                            "error",
                          );
                        }
                      },
                    });
                  }}
                >
                  <Trash2 size={9} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Row
          canEdit={canEdit}
          co={co}
        icon={Circle}
        title={t("cdd.row.sof", { fallback: "Source of wealth" })}
        status={state.sofStatus}
        vid={state.sofVerifiedById}
        vat={state.sofVerifiedAt}
        onEdit={() => setModal({ kind: "sof" })}
      >
        {state.sourceOfWealth && (
          <span style={{ color: "var(--tp)" }}>
            {state.sourceOfWealth.slice(0, 100)}
            {state.sourceOfWealth.length > 100 ? "…" : ""}
          </span>
        )}
      </Row>

      {modal?.kind === "identity" && (
        <IdentityModal
          state={state}
          client={client}
          onCancel={() => setModal(null)}
          onSave={patchState}
        />
      )}
      {modal?.kind === "pep" && (
        <PepModal
          state={state}
          onCancel={() => setModal(null)}
          onSave={patchState}
        />
      )}
      {modal?.kind === "sof" && (
        <SofModal
          state={state}
          onCancel={() => setModal(null)}
          onSave={patchState}
        />
      )}
      {modal?.kind === "ubos_status" && (
        <UbosStatusModal
          state={state}
          onCancel={() => setModal(null)}
          onSave={patchState}
        />
      )}
      {(modal?.kind === "ubo_new" || modal?.kind === "ubo_edit") && (
        <UboModal
          row={modal.row}
          clientId={client.id}
          onCancel={() => setModal(null)}
          onSaved={(newRow: any, isUpdate: any) => {
            setState((prev: any) => ({
              ...prev,
              ubos: isUpdate
                ? prev.ubos.map((x: any) => (x.id === newRow.id ? newRow : x))
                : [...(prev.ubos || []), newRow],
            }));
            setModal(null);
          }}
        />
      )}
    </div>
  );
}

function IdentityModal({ state, client, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    identityStatus: state.identityStatus || "required",
  });
  return (
    <RowModal
      title={t("cdd.row.identity", { fallback: "Identity verification" })}
      onCancel={onCancel}
      onSave={() => onSave(f)}
    >
      <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "4px 0 8px" }}>
        {(client?.type || "").toLowerCase() === "company"
          ? `Identification of legal person ${client.companyName || ""} via Enterprise number / statutes. Attach via the Documents section.`
          : `Identification of natural person ${[client?.firstName, client?.lastName].filter(Boolean).join(" ")}. ID details live on the Contact Person record.`}
      </p>
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.identityStatus}
          onChange={(v: any) => setF((p) => ({ ...p, identityStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function PepModal({ state, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    pepStatus: state.pepStatus || "required",
    pepIsPep: state.pepIsPep ?? false,
    pepScreenedAt: state.pepScreenedAt
      ? new Date(state.pepScreenedAt).toISOString().slice(0, 10)
      : "",
    pepNotes: state.pepNotes || "",
  });
  return (
    <RowModal
      title={t("cdd.row.pep", { fallback: "PEP screen" })}
      onCancel={onCancel}
      onSave={() =>
        onSave({
          pepStatus: f.pepStatus,
          pepIsPep: f.pepIsPep,
          pepScreenedAt: f.pepScreenedAt || null,
          pepNotes: f.pepNotes || null,
        })
      }
    >
      <div className="field">
        <label>
          <input
            type="checkbox"
            checked={f.pepIsPep}
            onChange={(e) =>
              setF((p) => ({ ...p, pepIsPep: e.target.checked }))
            }
          />{" "}
          {t("cdd.trigger.pep", { fallback: "PEP flagged" })}
        </label>
      </div>
      <div className="field">
        <label>{t("cdd.field.screened_at", { fallback: "Screened at" })}</label>
        <input
          className="field-input"
          type="date"
          value={f.pepScreenedAt}
          onChange={(e) =>
            setF((p) => ({ ...p, pepScreenedAt: e.target.value }))
          }
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.notes", { fallback: "Notes" })}</label>
        <textarea
          className="field-input"
          value={f.pepNotes}
          onChange={(e) => setF((p) => ({ ...p, pepNotes: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.pepStatus}
          onChange={(v: any) => setF((p) => ({ ...p, pepStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function SofModal({ state, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    sofStatus: state.sofStatus || "required",
    sourceOfWealth: state.sourceOfWealth || "",
  });
  return (
    <RowModal
      title={t("cdd.row.sof", { fallback: "Source of wealth" })}
      onCancel={onCancel}
      onSave={() => onSave(f)}
    >
      <div className="field">
        <label>{t("cdd.field.sof", { fallback: "Narrative" })}</label>
        <textarea
          className="field-input"
          rows={5}
          value={f.sourceOfWealth}
          onChange={(e) =>
            setF((p) => ({ ...p, sourceOfWealth: e.target.value }))
          }
        />
      </div>
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.sofStatus}
          onChange={(v: any) => setF((p) => ({ ...p, sofStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function UbosStatusModal({ state, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState({ ubosStatus: state.ubosStatus || "required" });
  return (
    <RowModal
      title={t("cdd.row.ubo", { fallback: "UBO chain" })}
      onCancel={onCancel}
      onSave={() => onSave(f)}
    >
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.ubosStatus}
          onChange={(v: any) => setF((p) => ({ ...p, ubosStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function UboModal({ row, clientId, onCancel, onSaved }: any) {
  const { t } = useTranslation();
  const isEdit = !!row?.id;
  const [f, setF] = useState({
    firstName: row?.firstName || "",
    lastName: row?.lastName || "",
    dateOfBirth: row?.dateOfBirth
      ? new Date(row.dateOfBirth).toISOString().slice(0, 10)
      : "",
    nationality: row?.nationality || "",
    ownershipPct: row?.ownershipPct ?? "",
    controlType: row?.controlType || "direct_ownership",
    notes: row?.notes || "",
  });
  const save = async () => {
    if (!f.firstName.trim() && !f.lastName.trim()) {
      if (window.lxToast) window.lxToast("⚠ First or last name required");
      return;
    }
    const body = {
      clientId,
      firstName: f.firstName.trim() || null,
      lastName: f.lastName.trim() || null,
      dateOfBirth: f.dateOfBirth || null,
      nationality: f.nationality.trim() || null,
      ownershipPct: f.ownershipPct === "" ? null : Number(f.ownershipPct),
      controlType: f.controlType || null,
      notes: f.notes.trim() || null,
    };
    try {
      const res = isEdit
        ? await api.patch("/cdd/ubo/" + row.id, body)
        : await api.post("/cdd/ubo", body);
      onSaved(res, isEdit);
    } catch (e: any) {
      if (window.lxToast) window.lxToast("⚠ " + (e.message || "Save failed"));
    }
  };
  return (
    <RowModal
      title={t("cdd.row.ubo", { fallback: "UBO" })}
      onCancel={onCancel}
      onSave={save}
    >
      <div className="field">
        <label>{t("cdd.field.first_name", { fallback: "First name" })}</label>
        <input
          className="field-input"
          value={f.firstName}
          onChange={(e) => setF((p) => ({ ...p, firstName: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.last_name", { fallback: "Last name" })}</label>
        <input
          className="field-input"
          value={f.lastName}
          onChange={(e) => setF((p) => ({ ...p, lastName: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.dob", { fallback: "Date of birth" })}</label>
        <input
          className="field-input"
          type="date"
          value={f.dateOfBirth}
          onChange={(e) => setF((p) => ({ ...p, dateOfBirth: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.nationality", { fallback: "Nationality" })}</label>
        <input
          className="field-input"
          value={f.nationality}
          onChange={(e) => setF((p) => ({ ...p, nationality: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>
          {t("cdd.field.ownership_pct", { fallback: "Ownership %" })}
        </label>
        <input
          className="field-input"
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={f.ownershipPct}
          onChange={(e) =>
            setF((p) => ({ ...p, ownershipPct: e.target.value }))
          }
        />
      </div>
      <div className="field">
        <label>
          {t("cdd.field.control_type", { fallback: "Control type" })}
        </label>
        <MenuSelect
          className="field-input"
          value={f.controlType}
          onChange={(v) => setF((p) => ({ ...p, controlType: v }))}
          options={[
            [
              "direct_ownership",
              t("cdd.control.direct", { fallback: "Direct ownership" }),
            ],
            [
              "indirect_ownership",
              t("cdd.control.indirect", { fallback: "Indirect ownership" }),
            ],
            [
              "control_other_means",
              t("cdd.control.other_means", {
                fallback: "Control by other means",
              }),
            ],
            [
              "senior_management_fallback",
              t("cdd.control.senior_mgmt", {
                fallback: "Senior management (fallback)",
              }),
            ],
          ]}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.notes", { fallback: "Notes" })}</label>
        <textarea
          className="field-input"
          value={f.notes}
          onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))}
        />
      </div>
    </RowModal>
  );
}
